/**
 * OnPoint "Analytical Field" — shared deterministic geometry.
 * One visual grammar for the isopleth/contour field + the dimensional radar.
 * NO Math.random — every value is a closed-form function of its inputs.
 * Rebuilt for the OWNER REFERENCE: 8-axis radar + broad topographic terrain planes.
 */

export const FIELD_W = 1200;
export const FIELD_H = 800;
export const FOCAL = { x: 0.72 * FIELD_W, y: 0.52 * FIELD_H }; // (864, 416) — right-of-center

// Palette (memo tokens, kept as literals for the SVG subsystem)
export const C = {
  field: '#F6F4EE',
  paper: '#FCFBF7',
  sage: '#6E9A82', // desaturated pale sage (readable as translucent membrane)
  sageLight: '#9DB8A0', // very pale sage — broad planes & high relief
  green: '#4C7F68', // deep but muted sage — depth lines
  greenDeep: '#2E5A4A', // muted darker sage / grey-green — structure
  ink: '#2A2A28', // graphite
  inkMid: '#5F5C55', // warm grey — labels
  ink2: '#8A877E', // light hairline grey
  cream: '#F4EFE4', // cream / off-white highlight
  warn: '#B0472F',
  coral: '#C06A4A', // thin muted terracotta — single signal
};

// Smooth closed path: polar points → Catmull-Rom → cubic Bézier.
// `sharpen` (0..1) pulls the curve tighter between vertices: 0 = relaxed organic,
// higher = more axis-driven (less inflated between nodes). Default ~0.
function smoothClosedPath(pt, sharpen = 0) {
  const n = pt.length;
  const k = 6 - sharpen * 3; // 6 (relaxed) → 3 (sharp)
  let d = `M${pt[0][0].toFixed(1)} ${pt[0][1].toFixed(1)}`;
  for (let i = 0; i < n; i += 1) {
    const p0 = pt[(i - 1 + n) % n];
    const p1 = pt[i];
    const p2 = pt[(i + 1) % n];
    const p3 = pt[(i + 2) % n];
    const c1x = p1[0] + (p2[0] - p0[0]) / k;
    const c1y = p1[1] + (p2[1] - p0[1]) / k;
    const c2x = p2[0] - (p3[0] - p1[0]) / k;
    const c2y = p2[1] - (p3[1] - p1[1]) / k;
    d += `C${c1x.toFixed(1)} ${c1y.toFixed(1)} ${c2x.toFixed(1)} ${c2y.toFixed(1)} ${p2[0].toFixed(1)} ${p2[1].toFixed(1)}`;
  }
  return `${d}Z`;
}

// Irregular closed ring (the field's contour iso-lines / broad planes).
export function ringPath(cx, cy, R, phi, seed, amp = 0.055) {
  const M = 80;
  const pt = [];
  for (let j = 0; j < M; j += 1) {
    const th = (j / M) * Math.PI * 2;
    const k =
      1 +
      amp * Math.sin(3 * th + phi) +
      amp * 0.8 * Math.sin(5 * th + phi * 2.1) +
      amp * 0.9 * Math.sin(2 * th + seed);
    pt.push([cx + R * k * Math.cos(th), cy + R * k * Math.sin(th)]);
  }
  return smoothClosedPath(pt);
}

// Organic N-vertex polygon surface. Gentle wobble so it reads as terrain membrane.
export function pentagonPath(cx, cy, R, phase, vertices = 8) {
  const M = vertices * 6;
  const pt = [];
  for (let j = 0; j < M; j += 1) {
    const th = (j / M) * Math.PI * 2;
    const k =
      1 + 0.03 * Math.sin(vertices * th + phase) + 0.018 * Math.sin(2 * vertices * th + phase * 1.7);
    pt.push([cx + R * k * Math.cos(th), cy + R * k * Math.sin(th)]);
  }
  return smoothClosedPath(pt);
}

// The current-state deviation surface — irregular 8-axis organic form.
export function profilePolygonPath(cx, cy, R, profile, phase) {
  const pt = RADAR_ANGLES.map((a, k) => {
    const kk = 1 + 0.018 * Math.sin(phase + k * 1.7);
    const r = R * profile[k] * kk;
    return [cx + r * Math.cos(a), cy + r * Math.sin(a)];
  });
  return smoothClosedPath(pt);
}

// 8-lobe membrane with richer per-lobe undulation (for ambient breathing).
export function lobePath(cx, cy, R, profile, phase) {
  const M = 8 * 8;
  const pt = [];
  for (let j = 0; j < M; j += 1) {
    const th = (j / M) * Math.PI * 2;
    const seg = th / (Math.PI * 2) * 8;
    const k0 = Math.floor(seg) % 8;
    const k1 = (k0 + 1) % 8;
    const t = seg - Math.floor(seg);
    const rr = R * (profile[k0] + (profile[k1] - profile[k0]) * t);
    const und =
      1 +
      0.05 * Math.sin(8 * th + phase) +
      0.032 * Math.sin(16 * th + phase * 1.4) +
      0.02 * Math.sin(3 * th - phase * 0.6);
    pt.push([cx + rr * und * Math.cos(th), cy + rr * und * Math.sin(th)]);
  }
  return smoothClosedPath(pt);
}

/**
 * Directional height-field kernel. Given an angle, returns a smooth 0..1 "elevation"
 * that is warped by the 8 domain values: the perimeter bends toward higher-value
 * domains, and the field is denser near steep transitions, broader on flats.
 * `scaleR` = fraction of R resolved so far (0 center → 1 perimeter).
 */
function heightField(th, profile, scaleR) {
  // smooth angular interpolation of the 8 values (0..1 each) — periodic
  let v = 0;
  for (let k = 0; k < 8; k += 1) {
    const a = RADAR_ANGLES[k];
    v += (0.5 + 0.5 * Math.cos(th - a)) * profile[k];
  }
  v = v / 4; // 0.5 center-ish … ~1 near clusters of high domains
  // warp: dominant domain pulls the radius outward here, gentle sag between — STRONGER
  const warp = 1 + (v - 0.7) * 0.32; // ±~10% (was ±6%) → the 8 directions visibly pull
  // slope compression: denser spacing where the field changes fast (gradient proxy) —
  // a little stronger so contours crowd near steep transitions
  const gradient = Math.abs(Math.sin(th * 8 + 0.3)) * (v - 0.6);
  const compression = 1 + gradient * 0.8 * scaleR;
  return { warp, compression };
}

/**
 * THE single-source radar membrane — SHARPENED so the 8 axis values are readable
 * from the silhouette (less inflated between nodes). Still elegant, not a hard
 * polygon. Same vertex set drives primary, coral, nodes, and relief.
 */
export function radarMembranePath(cx, cy, R, profile, phase, sharpen = 0.45) {
  const pt = RADAR_ANGLES.map((a, k) => {
    const wob = 1 + 0.008 * Math.sin(phase + k * 1.7);
    const r = R * profile[k] * wob;
    return [cx + r * Math.cos(a), cy + r * Math.sin(a)];
  });
  return smoothClosedPath(pt, sharpen);
}

/**
 * Nested relief as a true DIRECTIONAL HEIGHT FIELD. Each level is an intermediate
 * radar surface grown from the SAME 8-value profile, but warped by the height field
 * so contours are asymmetric: locally compressed near steep transitions, broader on
 * flats, bending toward the high-value domains. `light` returns a 0..1 term per level
 * describing its exposure to a fixed light source (upper-right) for the renderer.
 */
export function radarRelief(cx, cy, R, profile, phase, count = 16) {
  const levels = [];
  for (let i = 1; i <= count; i += 1) {
    const f = i / (count + 1); // ~0.059 … 0.941
    const base = profile.map((v) => f * (0.9 + 0.14 * v)); // strong value → bulges sooner
    const pt = [];
    // sample densely so the warp produces smooth directional deformation
    for (let j = 0; j < 96; j += 1) {
      const th = (j / 96) * Math.PI * 2;
      // angular interpolation of the base profile (piecewise between domain points)
      const seg = th / (Math.PI * 2) * 8;
      const k0 = Math.floor(seg) % 8;
      const k1 = (k0 + 1) % 8;
      const t = seg - Math.floor(seg);
      const rad = R * (base[k0] + (base[k1] - base[k0]) * t);
      const { warp, compression } = heightField(th, profile, f);
      // compression: push contour inward where gradient is steep → asymmetric spacing
      const rr = rad * warp * (1 - compression * 0.06);
      const phaseOff = phase + f * 2.0;
      const wobble = 1 + 0.012 * Math.sin(3 * th + phaseOff) + 0.008 * Math.sin(7 * th - phaseOff * 0.6);
      pt.push([cx + rr * wobble * Math.cos(th), cy + rr * wobble * Math.sin(th)]);
    }
    // light exposure from the height-field (raw 0.05..1 — fractalLight already spans the
    // full brightness range, giving a real dark basin + cream ridge)
    const light = fractalLight(f, RADAR_PROFILE);
    levels.push({ f, d: smoothClosedPath(pt, 0.25), light });
  }
  return levels;
}

// deterministic pseudo-light for a relief level: bright ridge toward the upper-right
// domains (Demand & Forecast → Coverage/Schedule), dark basin toward lower-center
// (Queue → Behavior). The radial term + directional term give real asymmetric contrast,
// not a uniform radial fade — no Math.random.
function fractalLight(f, profile) {
  // directional: upper-right domains lift the light, lower-center drag it down
  const upper = profile[0] * 0.4 + profile[1] * 0.4 + profile[2] * 0.2; // top→right
  const lower = profile[4] * 0.4 + profile[5] * 0.4 + profile[3] * 0.2; // bottom→left
  const dir = (upper - lower) * 1.6; // ~ -0.18..+0.2 — stronger directional lighting
  // radial: centre + lower rim fall to a dark shadow basin; upper outer rim brightens
  const radial = (f - 0.5) * 0.9;
  return Math.min(1, Math.max(0.05, 0.3 + dir + radial * 0.85));
}

// ── 8-axis radar ────────────────────────────────────────────────
export const RADAR_R = 204; // slightly reduced → more breathing room around the radar
// vertex-up, 45° spacing: top, top-right, right, bottom-right, bottom, bottom-left, left, top-left
export const RADAR_ANGLES = Array.from({ length: 8 }, (_, k) => ((-90 + 45 * k) * Math.PI) / 180);
export const RADAR_PHASE = 0.9;

// Illustrative, deterministic deviation profile — authored to reproduce the reference
// silhouette (irregular, sculptural). No numerals ever rendered.
export const RADAR_PROFILE = [0.88, 0.62, 0.75, 0.56, 0.62, 0.72, 0.58, 0.84];

// The 8 hero domain labels (hero-visual taxonomy ONLY). Verbatim set.
export const DOMAIN_LABELS = [
  'Demand & Forecast', // top
  'Coverage', // top-right
  'Schedule', // right
  'Intraday', // bottom-right
  'Queue', // bottom
  'Behavior', // bottom-left
  'Execution', // left
  'Cost & Capacity', // top-left
];
export const DOMAIN_FULL = DOMAIN_LABELS; // re-export for consumers

// ── OUTER TERRAIN — the buoyant spatial landscape the radar sits INSIDE ──
// Not just thin contours: broad overlapping sage sheets + a sweeping upper-right plane
// + a diagonal crest flowing toward the radar + one distant plane with a soft coral edge.
// Each entry: path, kind ('deep' | 'broad' | 'crest' | 'edge'), opacity base.
// Opacities are dialed DOWN (vs prior pass) so the outer field supports the radar
// rather than competing — item 3: LEFT COPY → RADAR RELIEF → OUTER TERRAIN.
export const BROAD_PLANES = [
  // deep foundation layer — large, soft, low
  { d: ringPath(FIELD_W * 0.6, FIELD_H * 0.62, 560, 0.3, 0.7, 0.14), kind: 'deep', op: 0.04 },
  { d: ringPath(FIELD_W * 0.85, FIELD_H * 0.5, 480, 4.0, 2.3, 0.13), kind: 'deep', op: 0.04 },
  // large sweeping upper-right plane (the "premium" mass) — kept but soft
  { d: ringPath(FIELD_W * 1.02, FIELD_H * 0.28, 520, 1.7, 1.2, 0.13), kind: 'broad', op: 0.065 },
  // mid sheets overlapping toward the radar
  { d: ringPath(FIELD_W * 0.9, FIELD_H * 0.46, 410, 2.4, 0.4, 0.12), kind: 'broad', op: 0.05 },
  { d: ringPath(FIELD_W * 0.66, FIELD_H * 0.56, 430, 5.6, 2.9, 0.11), kind: 'broad', op: 0.055 },
  // diagonal crest flowing from top/right toward the radar (light fold)
  { d: ringPath(FIELD_W * 0.92, FIELD_H * 0.4, 330, 3.1, 1.5, 0.12), kind: 'crest', op: 0.045 },
  // one distant outer plane — carries a restrained warm coral edge
  { d: ringPath(FIELD_W * 0.74, FIELD_H * 0.5, 500, 0.9, 2.1, 0.13), kind: 'edge', op: 0.035 },
];

export const CONTOURS = Array.from({ length: 8 }, (_, i) => {
  // concentric organic contours radiating from the focal (the analytical landscape)
  const R = 120 + i * 78;
  return {
    d: ringPath(FOCAL.x + (i - 3) * 14, FOCAL.y + (i - 3) * 6, R, i * 1.27, i * 0.7),
    i,
  };
});

export const RUPTURE =
  'M785 292 C812 322 828 356 812 428 L804 424 C818 356 802 326 778 300 Z';